import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'

import styles from './Field.module.css'

/**
 * `?: string | undefined` rather than `?: string`.
 *
 * Under `exactOptionalPropertyTypes` those differ: the bare form means "absent
 * or a string" and rejects an explicit `undefined`. These props are forwarded
 * from a wrapper that destructures them, so an unset one *does* arrive as an
 * explicit `undefined` — the wider form is what makes forwarding legal without
 * every caller rebuilding the object to omit keys.
 */
interface FieldShellProps {
  readonly label: string
  readonly hint?: string | undefined
  /**
   * A validation message.
   *
   * Core returns errors as values rather than throwing (ADR-0005), so what
   * arrives here is a rendered `ValidationError` message. The field's job is to
   * associate it with the control and mark the control invalid — not to decide
   * what is valid.
   */
  readonly error?: string | undefined
  readonly optional?: boolean | undefined
  readonly children: (ids: {
    controlId: string
    describedBy: string | undefined
    invalid: boolean
  }) => ReactNode
}

/**
 * Label, hint, error and the wiring between them.
 *
 * The ids are generated here and handed to the control through a render prop,
 * because `aria-describedby` has to name elements that exist. Every field
 * writing its own `useId` calls and remembering to thread them is how one
 * control ends up with an unassociated error message that a screen reader
 * never reads out.
 */
function FieldShell({ label, hint, error, optional = false, children }: FieldShellProps) {
  const controlId = useId()
  const hintId = `${controlId}-hint`
  const errorId = `${controlId}-error`

  const describedBy =
    [hint === undefined ? '' : hintId, error === undefined ? '' : errorId]
      .filter(Boolean)
      .join(' ') || undefined

  return (
    <div className={styles['field']}>
      <label className={styles['label']} htmlFor={controlId}>
        {label}
        {optional && <span className={styles['optional']}> (optional)</span>}
      </label>
      {children({ controlId, describedBy, invalid: error !== undefined })}
      {hint !== undefined && (
        <span id={hintId} className={styles['hint']}>
          {hint}
        </span>
      )}
      {error !== undefined && (
        // `role="alert"` so a validation failure is announced when it appears,
        // rather than only being found by someone who navigates back to it.
        <span id={errorId} className={styles['error']} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

type ShellProps = Omit<FieldShellProps, 'children'>

export function TextField({
  label,
  hint,
  error,
  optional,
  ...rest
}: ShellProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'id'>) {
  return (
    <FieldShell {...{ label, hint, error, optional }}>
      {({ controlId, describedBy, invalid }) => (
        <input
          id={controlId}
          type="text"
          className={`${styles['control']} ${invalid ? styles['invalid'] : ''}`}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  )
}

export function TextArea({
  label,
  hint,
  error,
  optional,
  ...rest
}: ShellProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'>) {
  return (
    <FieldShell {...{ label, hint, error, optional }}>
      {({ controlId, describedBy, invalid }) => (
        <textarea
          id={controlId}
          className={`${styles['control']} ${styles['textarea']} ${invalid ? styles['invalid'] : ''}`}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        />
      )}
    </FieldShell>
  )
}

export function Select({
  label,
  hint,
  error,
  optional,
  children,
  ...rest
}: ShellProps & Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'>) {
  return (
    <FieldShell {...{ label, hint, error, optional }}>
      {({ controlId, describedBy, invalid }) => (
        <select
          id={controlId}
          className={`${styles['control']} ${styles['select']} ${invalid ? styles['invalid'] : ''}`}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          {...rest}
        >
          {children}
        </select>
      )}
    </FieldShell>
  )
}
